import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/** The themes a user can choose between. */
export const THEME_PREFERENCES = {
  light: 'light',
  dark: 'dark',
  system: 'system',
} as const

export type ThemePreference = (typeof THEME_PREFERENCES)[keyof typeof THEME_PREFERENCES]

type ThemePreferenceContextValue = {
  themePreference: ThemePreference
  setThemePreference: (theme: ThemePreference) => void
}

/** Browser storage key holding the chosen theme. */
export const THEME_PREFERENCE_STORAGE_KEY = 'calendly_publisher_theme_preference'

/** Default when nothing valid is stored, so a first-time user follows their device. */
const DEFAULT_THEME_PREFERENCE = THEME_PREFERENCES.system

const isThemePreference = (value: unknown): value is ThemePreference => Object.values(THEME_PREFERENCES).includes(value as ThemePreference)

const defaultContextValue: ThemePreferenceContextValue = {
  themePreference: DEFAULT_THEME_PREFERENCE,
  setThemePreference: () => {},
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue>(defaultContextValue)

/** A stored choice always wins, so the default only applies to someone who has never chosen. */
function readFromStorage(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY)
    if (isThemePreference(stored)) return stored
  } catch {
    // localStorage may be unavailable (e.g. private browsing with strict settings)
  }
  return DEFAULT_THEME_PREFERENCE
}

/**
 * Provides persisted theme preference state to the app.
 * Mount once near the app root. Persists to localStorage and syncs across tabs.
 */
export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(readFromStorage)

  const setThemePreference = useCallback((theme: ThemePreference) => {
    try {
      localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, theme)
    } catch {
      // ignore write failures
    }
    setThemePreferenceState(theme)
  }, [])

  // Sync changes made in other tabs
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== THEME_PREFERENCE_STORAGE_KEY) return
      const value = event.newValue
      if (isThemePreference(value)) setThemePreferenceState(value)
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const value = useMemo(
    () => ({ themePreference, setThemePreference }),
    [themePreference, setThemePreference]
  )

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  )
}

export function useThemePreference(): ThemePreferenceContextValue {
  return useContext(ThemePreferenceContext)
}
